import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutGrid,
  Bell,
  Settings,
  LogOut,
  Users,
  Package,
  ShieldCheck,
  IceCream2,
  Popsicle,
  Store,
  Truck,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import InstallPWAButton from "@/components/InstallPWAButton";
import InstallPWABanner from "@/components/InstallPWABanner";
import PushNotificationSettings from "@/components/PushNotificationSettings";
import DigestTimeSettings from "@/components/DigestTimeSettings";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isManager = !!user?.role?.is_manager;
  const [configOpen, setConfigOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Main tabs shown in nav — filtered by user.tabs (managers see everything)
  const userTabs = user?.tabs || [];
  const canSee = (tab) => isManager || userTabs.includes(tab);
  const mainNav = [
    canSee("piso") && { to: "/piso", label: "Piso", icon: LayoutGrid, testid: "nav-piso" },
    canSee("produccion") && { to: "/produccion", label: "Producción", icon: Package, testid: "nav-produccion" },
    canSee("helados") && { to: "/helados", label: "Helados", icon: IceCream2, testid: "nav-helados" },
    canSee("paletas") && { to: "/paletas", label: "Paletas", icon: Popsicle, testid: "nav-paletas" },
    isManager && { to: "/transporte", label: "Transporte", icon: Truck, testid: "nav-transporte" },
    isManager && { to: "/pendientes", label: "Pendientes", icon: Bell, testid: "nav-pendientes" },
  ].filter(Boolean);

  const canManageInventory = isManager || !!user?.can_manage_inventory;
  const adminNav = [
    { to: "/admin/items", label: "Items", icon: Package, testid: "nav-items", editor: true },
    { to: "/admin/helados", label: "Sabores", icon: IceCream2, testid: "nav-sabores", editor: true },
    { to: "/admin/paletas", label: "Paletas", icon: Popsicle, testid: "nav-admin-paletas", editor: true },
    { to: "/admin/usuarios", label: "Usuarios", icon: Users, testid: "nav-usuarios", editor: false },
    { to: "/admin/roles", label: "Roles", icon: ShieldCheck, testid: "nav-roles", editor: false },
    { to: "/admin/sucursales", label: "Sucursales", icon: Store, testid: "nav-sucursales", editor: false },
  ].filter((it) => isManager || (canManageInventory && it.editor));

  const sucursalLabel = user?.sucursal?.name || (isManager ? "Todas las sucursales" : "");

  const ConfigMenu = ({ compact = false }) => (
    <Sheet open={configOpen} onOpenChange={setConfigOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="config-menu-button"
          aria-label="Configuración"
          className={
            compact
              ? "inline-flex items-center justify-center h-10 w-10 rounded-full text-[#4A3B32] hover:bg-[#FCF9F2] transition-colors"
              : "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-[#4A3B32] hover:bg-[#FCF9F2] transition-colors"
          }
        >
          <Settings className={compact ? "w-5 h-5" : "w-4 h-4"} />
          {!compact && <span>Config</span>}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80 bg-white border-l border-[#EADBCE] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-[#4A3B32]">Configuración</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-1">
          {adminNav.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={() => setConfigOpen(false)}
              data-testid={`${it.testid}-drawer`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-colors ${
                  isActive ? "bg-[#FFF0F0] text-[#FF5C5C]" : "text-[#4A3B32] hover:bg-[#FCF9F2]"
                }`
              }
            >
              <it.icon className="w-5 h-5" />
              {it.label}
            </NavLink>
          ))}
        </div>
        <div className="mt-8 pt-6 border-t border-[#EADBCE] space-y-6">
          {isManager && (
            <>
              <div>
                <p className="font-display font-bold text-[#4A3B32] mb-2">Notificaciones</p>
                <PushNotificationSettings />
              </div>
              <div>
                <p className="font-display font-bold text-[#4A3B32] mb-2">Preferencias</p>
                <DigestTimeSettings />
              </div>
            </>
          )}
          <InstallPWAButton />
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="min-h-screen bg-[#FCF9F2]">
      {/* Top bar */}
      <header className="border-b border-[#EADBCE] bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link
            to="/"
            className="flex items-center gap-2 font-display font-extrabold text-[#4A3B32] shrink-0"
            data-testid="logo-link"
          >
            <img
              src="/logo-wide.png?v=1"
              alt="Darkar Co. Paletas y Helados"
              className="h-10 sm:h-11 w-auto object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {mainNav.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors ${
                    isActive ? "bg-[#FFF0F0] text-[#FF5C5C]" : "text-[#4A3B32] hover:bg-[#FCF9F2]"
                  }`
                }
              >
                <it.icon className="w-4 h-4" />
                {it.label}
              </NavLink>
            ))}
            {(isManager || canManageInventory) && <ConfigMenu />}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {sucursalLabel && (
              <div
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F7C8E0]/40 text-[#4A3B32] text-xs font-bold"
                data-testid="current-sucursal"
              >
                <Store className="w-3.5 h-3.5" />
                {sucursalLabel}
              </div>
            )}
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-bold text-[#4A3B32]" data-testid="current-user-name">
                {user?.name}
              </span>
              <span className="text-xs text-[#8A796F]" data-testid="current-user-role">
                {user?.role?.name}
              </span>
            </div>
            {/* Compact Config icon — visible on mobile too (managers + editors) */}
            {(isManager || canManageInventory) && (
              <div className="md:hidden">
                <ConfigMenu compact />
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-[#4A3B32] hover:bg-[#FCF9F2] rounded-full"
            >
              <LogOut className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-28 md:pb-10">
        {sucursalLabel && (
          <div
            className="sm:hidden flex items-center gap-1.5 mb-4 text-xs font-bold text-[#4A3B32]"
            data-testid="current-sucursal-mobile"
          >
            <Store className="w-3.5 h-3.5" />
            <span>{sucursalLabel}</span>
          </div>
        )}
        <InstallPWABanner />
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-50 md:hidden border-t border-[#EADBCE] bg-white/95 backdrop-blur-xl">
        <div className="flex justify-around items-center h-16 px-1">
          {mainNav.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`${it.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-1 py-2 rounded-xl text-[10px] font-semibold transition-colors flex-1 min-w-0 ${
                  isActive ? "text-[#FF5C5C] bg-[#FFF0F0]" : "text-[#8A796F]"
                }`
              }
            >
              <it.icon className="w-5 h-5 shrink-0" />
              <span className="w-full text-center leading-tight">{it.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <Toaster position="top-center" richColors />
    </div>
  );
}
